import { Head, Link, usePage } from "@inertiajs/react";
import styled from "styled-components";
import SectionErrorBoundary from "../../homes/SectionErrorBoundary";
import PortalApp from "../../portal/App";
import {
  PortalContainer,
  PortalSection,
  PortalCard,
  PortalEmpty,
} from "../../portal/PortalCard";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  narrowScreenMediaQuery,
} from "../../constants/styles";

export default function Activity() {
  const {
    insights,
    resume,
    schedule,
    trending = [],
    bookmarks = [],
    cert_nudge: certNudge,
  } = usePage().props;

  return (
    <>
      <Head>
        <title>Aktivitas — Taxsam Learning Center</title>
        <meta
          name="description"
          content="Lanjutkan belajar, lihat jadwal kegiatan, materi trending, dan bookmark Anda."
        />
      </Head>
      <PortalContainer>
        {certNudge && (
          <CertNudge href={certNudge.url}>
            <CertNudgeIcon aria-hidden="true">🏆</CertNudgeIcon>
            <CertNudgeBody>
              <strong>{certNudge.count}</strong> sertifikat siap diklaim
            </CertNudgeBody>
            <CertNudgeArrow aria-hidden="true">→</CertNudgeArrow>
          </CertNudge>
        )}

        {insights && (
          <SectionErrorBoundary name="ActivityInsights">
            <InsightGrid>
              <InsightCard>
                <InsightLabel>Materi Ditonton</InsightLabel>
                <InsightValue>{insights.lessons_watched ?? 0}</InsightValue>
                <InsightHint>Total lesson yang sudah Anda akses.</InsightHint>
              </InsightCard>
              <InsightCard>
                <InsightLabel>Ujian Lulus</InsightLabel>
                <InsightValue>{insights.exams_passed ?? 0}</InsightValue>
                <InsightHint>Modul yang sudah Anda lulusi.</InsightHint>
              </InsightCard>
              <InsightCard>
                <InsightLabel>Sertifikat Disetujui</InsightLabel>
                <InsightValue>{insights.certificates ?? 0}</InsightValue>
                <InsightHint>Sertifikat yang sudah diterbitkan.</InsightHint>
              </InsightCard>
              <InsightCard $emphasis>
                <InsightLabel>Skor Rata-rata</InsightLabel>
                <InsightValue>
                  {insights.avg_score != null ? insights.avg_score : "—"}
                </InsightValue>
                <InsightHint>Berdasarkan ujian Anda.</InsightHint>
              </InsightCard>
            </InsightGrid>
          </SectionErrorBoundary>
        )}

        {resume && resume.url && (
          <SectionErrorBoundary name="ResumeCard">
            <ResumeSection>
              <ResumeEyebrow>Lanjut Belajar</ResumeEyebrow>
              <ResumeTitle>{resume.lesson_name}</ResumeTitle>
              {resume.curriculum_name && resume.course_name && (
                <ResumeMeta>
                  {resume.course_name} › {resume.curriculum_name}
                </ResumeMeta>
              )}
              <ResumeCta href={resume.url}>Lanjutkan →</ResumeCta>
            </ResumeSection>
          </SectionErrorBoundary>
        )}

        <SectionErrorBoundary name="JadwalKegiatan">
          <PortalSection
            title="Jadwal Kegiatan"
            subtitle="Webinar dan kegiatan mendatang yang bisa Anda ikuti."
          >
            {schedule?.upcoming?.length > 0 ? (
              <ScheduleList>
                {schedule.upcoming.map((row) => (
                  <ScheduleRow
                    key={row.id}
                    as={row.url ? Link : "div"}
                    href={row.url || undefined}
                  >
                    <ScheduleDate>
                      {row.date_iso ? formatDateID(row.date_iso) : "—"}
                    </ScheduleDate>
                    <ScheduleBody>
                      <ScheduleTitle>{row.title}</ScheduleTitle>
                      {row.speaker && (
                        <ScheduleSpeaker>{row.speaker}</ScheduleSpeaker>
                      )}
                    </ScheduleBody>
                    {row.registered && (
                      <RegisteredPill>Terdaftar</RegisteredPill>
                    )}
                  </ScheduleRow>
                ))}
              </ScheduleList>
            ) : (
              <PortalEmpty>Belum ada jadwal kegiatan mendatang.</PortalEmpty>
            )}
          </PortalSection>
        </SectionErrorBoundary>

        <SectionErrorBoundary name="TrendingSekarang">
          <PortalSection
            title="Trending Sekarang"
            subtitle="Materi yang sedang banyak ditonton."
          >
            {trending.length > 0 ? (
              <CardGrid>
                {trending.map((row) => (
                  <TrendCard
                    key={row.id}
                    as={row.url ? Link : "div"}
                    href={row.url || undefined}
                  >
                    <TrendThumb
                      $hasImage={!!row.banner_url}
                      $url={row.banner_url}
                    >
                      {!row.banner_url && (
                        <TrendThumbFallback aria-hidden="true">
                          {(row.name || "?").trim().charAt(0).toUpperCase()}
                        </TrendThumbFallback>
                      )}
                    </TrendThumb>
                    <TrendBody>
                      <TrendName>{row.name}</TrendName>
                      {row.curriculum_name && (
                        <TrendMeta>{row.curriculum_name}</TrendMeta>
                      )}
                    </TrendBody>
                  </TrendCard>
                ))}
              </CardGrid>
            ) : (
              <PortalEmpty>Belum ada materi trending.</PortalEmpty>
            )}
          </PortalSection>
        </SectionErrorBoundary>

        <SectionErrorBoundary name="BookmarkRow">
          <PortalSection
            title="Bookmark"
            subtitle="Materi yang Anda simpan untuk diakses lagi."
          >
            {bookmarks.length > 0 ? (
              <CardGrid>
                {bookmarks.map((row) => (
                  <TrendCard
                    key={row.id}
                    as={row.url ? Link : "div"}
                    href={row.url || undefined}
                  >
                    <TrendThumb
                      $hasImage={!!row.banner_url}
                      $url={row.banner_url}
                    >
                      {!row.banner_url && (
                        <TrendThumbFallback aria-hidden="true">
                          {(row.name || "?").trim().charAt(0).toUpperCase()}
                        </TrendThumbFallback>
                      )}
                    </TrendThumb>
                    <TrendBody>
                      <TrendName>{row.name}</TrendName>
                    </TrendBody>
                  </TrendCard>
                ))}
              </CardGrid>
            ) : (
              <PortalEmpty>
                Belum ada bookmark. Simpan materi favorit dari halaman
                Sertifikasi.
              </PortalEmpty>
            )}
          </PortalSection>
        </SectionErrorBoundary>
      </PortalContainer>
    </>
  );
}

Activity.layout = (page) => <PortalApp>{page}</PortalApp>;

function formatDateID(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (_) {
    return iso;
  }
}

// ---- Styles ----------------------------------------------------------------

const InsightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
`;

const InsightCard = styled(PortalCard)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 18px 20px;
  ${({ $emphasis }) =>
    $emphasis &&
    `background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
     border-color: rgba(216, 89, 7, 0.32);`}

  [data-theme="dark"] & {
    ${({ $emphasis }) =>
      $emphasis &&
      `background: linear-gradient(135deg, rgba(216, 89, 7, 0.18), rgba(247, 179, 12, 0.08));
       border-color: rgba(216, 89, 7, 0.4);`}
  }
`;

const InsightLabel = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const InsightValue = styled.span`
  font-size: 28px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${brand_orange_deep};
  line-height: 1;
`;

const InsightHint = styled.span`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const CertNudge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  padding: 12px 18px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(216, 89, 7, 0.1),
    rgba(247, 179, 12, 0.1)
  );
  border: 1px solid rgba(216, 89, 7, 0.32);
  color: ${brand_orange_deep};
  text-decoration: none;
  font-weight: 700;
  font-size: 14px;
  width: fit-content;
  transition:
    filter 150ms ease,
    transform 100ms ease;

  strong {
    font-weight: 800;
  }
  &:hover {
    filter: brightness(0.97);
    transform: translateY(-1px);
    color: ${brand_orange_deep};
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: rgba(216, 89, 7, 0.15);
    color: #fdba74;
    &:hover {
      color: #fdba74;
    }
  }
`;

const CertNudgeIcon = styled.span`
  font-size: 20px;
  line-height: 1;
`;

const CertNudgeBody = styled.span`
  flex: 1;
  font-weight: 600;
`;

const CertNudgeArrow = styled.span`
  font-weight: 800;
`;

const ResumeSection = styled(PortalCard)`
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border: 1px solid rgba(216, 89, 7, 0.28);
  display: flex;
  flex-direction: column;
  gap: 8px;

  [data-theme="dark"] & {
    background: linear-gradient(
      135deg,
      rgba(216, 89, 7, 0.18),
      rgba(247, 179, 12, 0.1)
    );
    border-color: rgba(216, 89, 7, 0.38);
  }
`;

const ResumeEyebrow = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${brand_orange_deep};
`;

const ResumeTitle = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #1f2937;
  line-height: 1.3;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const ResumeMeta = styled.p`
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 700;

  [data-theme="dark"] & {
    color: #cbd5e1;
  }
`;

const ResumeCta = styled(Link)`
  display: inline-block;
  padding: 12px 26px;
  border-radius: 999px;
  background: ${brand_gradient};
  color: #ffffff;
  font-weight: 800;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-decoration: none;
  margin-top: 10px;
  width: fit-content;
  transition:
    filter 150ms ease,
    transform 100ms ease;

  &:hover {
    filter: brightness(0.97);
    transform: translateY(-1px);
    color: #fff;
  }
  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }
`;

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ScheduleRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr auto;
  gap: 16px;
  align-items: center;
  padding: 14px 18px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  text-decoration: none;
  color: inherit;
  transition:
    border-color 150ms ease,
    transform 100ms ease;

  &:hover {
    border-color: ${brand_orange_deep};
    transform: translateY(-1px);
  }

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

const ScheduleDate = styled.span`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${brand_orange_deep};
`;

const ScheduleBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ScheduleTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const ScheduleSpeaker = styled.span`
  font-size: 12px;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const RegisteredPill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.14);
  color: #047857;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
`;

const TrendCard = styled.a`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-decoration: none;
  color: inherit;
  transition: transform 150ms ease;

  &:hover {
    transform: translateY(-3px);
  }
`;

const TrendThumb = styled.div`
  aspect-ratio: 16 / 10;
  border-radius: 12px;
  background: ${({ $hasImage, $url }) =>
    $hasImage
      ? `center/cover no-repeat url("${$url}")`
      : "linear-gradient(135deg, #fcd34d, #fb923c)"};
  position: relative;
  box-shadow: ${card_shadow_md};
`;

const TrendThumbFallback = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.85);
`;

const TrendBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 4px;
`;

const TrendName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${brand_orange_deep};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TrendMeta = styled.span`
  font-size: 12px;
  color: #6b7280;

  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;
